import { useTreasureApi, ApiKeys } from 'hooks/api/use-treasure-api';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult,
} from 'react-query';
import { ReactQueryKeys } from 'enums';
import { ApprovalPolicy } from '../../team/hooks/use-approval-policy';

export interface ApprovalRequestPayload {
  body?: {
    amount?: number | string;
    person?: {
      email?: string;
    };
    cash?: AllocationSlice;
    smart?: AllocationSlice;
    tbills?: AllocationSlice;
    moneyMarket?: AllocationSlice;
    bankAccountId?: string;
  };
}

interface AllocationSlice {
  amount?: number;
  percentage?: number;
}

export interface ApprovalRequest {
  requestId: string;
  initiatedBy: string;
  requestType: string;
  payload: ApprovalRequestPayload | null;
  status: string;
  expiresAt: string;
  targetId: string | null;
}

export interface ApprovalRequestList {
  items: ApprovalRequest[];
}

export const useApprovalRequests = (): UseQueryResult<ApprovalRequestList> => {
  const { treasureApi } = useTreasureApi(ApiKeys.PortalGateway);

  return useQuery(ReactQueryKeys.ApprovalRequests, async () => {
    const { data } = await treasureApi().get<ApprovalRequestList>(
      '/approval-requests',
    );

    return {
      items: data.items ?? [],
    };
  });
};

const ENABLE_MULTIPLE_APPROVAL = 'ENABLE_MULTIPLE_APPROVAL';
const DISABLE_MULTIPLE_APPROVAL = 'DISABLE_MULTIPLE_APPROVAL';

const useApprovalRequestAction = (
  action: 'approve' | 'cancel',
): UseMutationResult<void, unknown, { requestId: string }> => {
  const queryClient = useQueryClient();
  const { treasureApi } = useTreasureApi(ApiKeys.PortalGateway);

  return useMutation(
    async ({ requestId }: { requestId: string }) => {
      await treasureApi().post(`/approval-requests/${requestId}/${action}`);
    },
    {
      onSuccess: (_data, { requestId }) => {
        const list = queryClient.getQueryData<ApprovalRequestList>(
          ReactQueryKeys.ApprovalRequests,
        );
        const requestType = list?.items.find(
          (item) => item.requestId === requestId,
        )?.requestType;
        const changesPolicy =
          requestType === ENABLE_MULTIPLE_APPROVAL ||
          requestType === DISABLE_MULTIPLE_APPROVAL;

        if (changesPolicy) {
          queryClient.setQueryData<ApprovalPolicy | undefined>(
            ReactQueryKeys.ApprovalPolicy,
            (current) => {
              if (!current || current.pendingRequestType !== requestType) {
                return current;
              }

              if (action === 'cancel') {
                return {
                  ...current,
                  pendingRequestType: null,
                };
              }

              return {
                ...current,
                enabled: requestType === ENABLE_MULTIPLE_APPROVAL,
                pendingRequestType: null,
              };
            },
          );
        }

        queryClient.refetchQueries(ReactQueryKeys.ApprovalRequests);
        queryClient.refetchQueries(ReactQueryKeys.ApprovalPolicy);

        if (action === 'approve' && requestType === 'REALLOCATION') {
          queryClient.refetchQueries(ReactQueryKeys.Portfolios);
        }
      },
    },
  );
};

export const useApproveApprovalRequest = (): UseMutationResult<
  void,
  unknown,
  { requestId: string }
> => useApprovalRequestAction('approve');

export const useCancelApprovalRequest = (): UseMutationResult<
  void,
  unknown,
  { requestId: string }
> => useApprovalRequestAction('cancel');
